import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  listDeliveryBoys,
  createCollection,
  updateCollection,
  getCollection,
  listMinGuarantees,
} from "@/lib/api";
import { formatINR, todayISO, DENOMINATIONS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useBranch } from "@/context/BranchContext";
import {
  ArrowLeft,
  Save,
  AlertTriangle,
  ShieldAlert,
  Calculator,
  Banknote,
} from "lucide-react";

const emptyForm = {
  delivery_boy_id: "",
  entry_date: todayISO(),
  cash_collected: "",
  earning: "",
  shop_deduction: "",
  other_deduction: "",
  paid_by_method: { cash: "", upi: "", bank: "", other: "" },
  denominations: DENOMINATIONS.reduce((a, d) => ({ ...a, [d]: 0 }), {}),
  notes: "",
  min_guarantee_preset_id: "",
  min_guarantee_override: "",
};

export default function CollectionForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [boys, setBoys] = useState([]);
  const [presets, setPresets] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { branchId } = useBranch();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [b, p] = await Promise.all([listDeliveryBoys(), listMinGuarantees()]);
        setBoys(b);
        setPresets(p.filter((x) => x.active !== false));
        if (isEdit) {
          const c = await getCollection(id);
          const denomMap = DENOMINATIONS.reduce((a, d) => ({ ...a, [d]: 0 }), {});
          (c.denominations || []).forEach((entry) => {
            denomMap[entry.denom] = entry.count || 0;
          });
          // Load paid_by_method with legacy fallback
          const pbmSrc = c.paid_by_method || {};
          const hasPbm = ["cash", "upi", "bank", "other"].some((k) => (pbmSrc[k] || 0) > 0);
          const pbm = hasPbm
            ? { cash: pbmSrc.cash || "", upi: pbmSrc.upi || "", bank: pbmSrc.bank || "", other: pbmSrc.other || "" }
            : {
                cash: c.payment_method === "cash" ? c.paid_amount || "" : "",
                upi: c.payment_method === "upi" ? c.paid_amount || "" : "",
                bank: c.payment_method === "bank" ? c.paid_amount || "" : "",
                other: c.payment_method === "other" ? c.paid_amount || "" : "",
              };
          setForm({
            delivery_boy_id: c.delivery_boy_id,
            entry_date: c.entry_date,
            cash_collected: c.cash_collected ?? "",
            earning: c.earning ?? "",
            shop_deduction: c.shop_deduction ?? "",
            other_deduction: c.other_deduction ?? "",
            paid_by_method: pbm,
            denominations: denomMap,
            notes: c.notes || "",
            min_guarantee_preset_id: c.min_guarantee_preset_id || "",
            min_guarantee_override: c.min_guarantee_preset_id ? "" : (c.min_guarantee ?? ""),
          });
        }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, branchId]);

  const num = (v) => parseFloat(v) || 0;
  const selectedBoy = boys.find((b) => b.id === form.delivery_boy_id);
  const selectedPreset = presets.find((p) => p.id === form.min_guarantee_preset_id);
  // Resolve effective min guarantee for the entry:
  // 1. explicit override input (>0), else 2. selected preset amount, else 3. boy's default
  const minGuarantee = (() => {
    const override = parseFloat(form.min_guarantee_override);
    if (!isNaN(override) && form.min_guarantee_override !== "") return override;
    if (selectedPreset) return selectedPreset.amount || 0;
    return selectedBoy?.min_guarantee || 0;
  })();
  const minGuaranteeSource = form.min_guarantee_override !== "" && !isNaN(parseFloat(form.min_guarantee_override))
    ? "Manual override"
    : selectedPreset
      ? `Preset · ${selectedPreset.name}`
      : selectedBoy
        ? "Boy default"
        : "";

  const totalPaid = useMemo(
    () =>
      num(form.paid_by_method.cash) +
      num(form.paid_by_method.upi) +
      num(form.paid_by_method.bank) +
      num(form.paid_by_method.other),
    [form.paid_by_method]
  );

  const finalPayable = useMemo(
    () =>
      num(form.cash_collected) -
      Math.max(num(form.earning), minGuarantee) -
      num(form.shop_deduction) -
      num(form.other_deduction),
    [form.cash_collected, form.earning, form.shop_deduction, form.other_deduction, minGuarantee]
  );

  const balance = useMemo(() => finalPayable - totalPaid, [finalPayable, totalPaid]);

  const shortfall = useMemo(
    () => Math.max(minGuarantee - num(form.earning), 0),
    [minGuarantee, form.earning]
  );

  const denomTotal = useMemo(
    () => DENOMINATIONS.reduce((sum, d) => sum + d * (parseInt(form.denominations[d]) || 0), 0),
    [form.denominations]
  );

  const cashPaid = num(form.paid_by_method.cash);
  const denomMatchesPaid = cashPaid === 0 || cashPaid === denomTotal;

  const setDenom = (d, v) => {
    const n = Math.max(0, parseInt(v) || 0);
    setForm((f) => ({ ...f, denominations: { ...f.denominations, [d]: n } }));
  };

  const useDenomAsPaid = () => {
    setForm((f) => ({ ...f, paid_by_method: { ...f.paid_by_method, cash: denomTotal } }));
  };

  const setPbm = (method, v) => {
    setForm((f) => ({ ...f, paid_by_method: { ...f.paid_by_method, [method]: v } }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.delivery_boy_id) {
      toast.error("Select a delivery boy");
      return;
    }
    if (!form.entry_date) {
      toast.error("Select a date");
      return;
    }
    if (cashPaid > 0 && denomTotal > 0 && !denomMatchesPaid) {
      toast.warning(
        `Denominations total ${formatINR(denomTotal)} does not match cash paid ${formatINR(cashPaid)}. Saving anyway.`
      );
    }
    setSaving(true);
    try {
      const pbm = {
        cash: num(form.paid_by_method.cash),
        upi: num(form.paid_by_method.upi),
        bank: num(form.paid_by_method.bank),
        other: num(form.paid_by_method.other),
      };
      const payload = {
        delivery_boy_id: form.delivery_boy_id,
        entry_date: form.entry_date,
        cash_collected: num(form.cash_collected),
        earning: num(form.earning),
        shop_deduction: num(form.shop_deduction),
        other_deduction: num(form.other_deduction),
        paid_amount: totalPaid,
        paid_by_method: pbm,
        payment_method: "cash", // server derives dominant/mixed
        denominations:
          pbm.cash > 0
            ? DENOMINATIONS.map((d) => ({ denom: d, count: parseInt(form.denominations[d]) || 0 })).filter(
                (x) => x.count > 0
              )
            : [],
        notes: form.notes || "",
        min_guarantee_preset_id: form.min_guarantee_preset_id || "",
        min_guarantee:
          form.min_guarantee_override !== "" && !isNaN(parseFloat(form.min_guarantee_override))
            ? parseFloat(form.min_guarantee_override)
            : selectedPreset
              ? selectedPreset.amount
              : (selectedBoy?.min_guarantee ?? 0),
      };
      if (isEdit) {
        await updateCollection(id, payload);
        toast.success("Entry updated");
      } else {
        await createCollection(payload);
        toast.success("Entry saved");
      }
      navigate("/collections");
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="collection-form-page">
      <div className="flex items-end justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate("/collections")}
            className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to entries
          </button>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-2">
            {isEdit ? "Edit Collection Entry" : "New Collection Entry"}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Enter today&apos;s cash collection and deductions. Payable and balance are computed live.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: inputs */}
        <div className="lg:col-span-8 space-y-6">
          {/* Boy + Date */}
          <section className="bg-white rounded-xl border border-zinc-200 p-6">
            <h2 className="font-display text-lg font-medium mb-4">Basic Info</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Delivery Boy</Label>
                <Select
                  value={form.delivery_boy_id}
                  onValueChange={(v) => setForm({ ...form, delivery_boy_id: v })}
                >
                  <SelectTrigger data-testid="form-delivery-boy-select">
                    <SelectValue placeholder="Select delivery boy" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {boys.length === 0 && (
                      <div className="px-3 py-2 text-xs text-zinc-500">
                        No delivery boys — add one first.
                      </div>
                    )}
                    {boys.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                        {b.min_guarantee > 0 && (
                          <span className="ml-2 text-xs text-zinc-500">
                            (MG {formatINR(b.min_guarantee)})
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedBoy && (
                  <p className="text-xs text-zinc-500">
                    Minimum Guarantee:{" "}
                    <span className="tabular font-medium text-zinc-900">
                      {formatINR(minGuarantee)}
                    </span>
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={form.entry_date}
                  onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
                  data-testid="form-date-input"
                  required
                />
              </div>
            </div>
          </section>

          {/* Amounts */}
          <section className="bg-white rounded-xl border border-zinc-200 p-6">
            <h2 className="font-display text-lg font-medium mb-4">Amounts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MoneyInput
                label="Cash Collected"
                value={form.cash_collected}
                onChange={(v) => setForm({ ...form, cash_collected: v })}
                testId="form-cash-collected-input"
                required
              />
              <div className="space-y-1.5">
                <Label>Earning</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  className="currency-input"
                  value={form.earning}
                  onChange={(e) => setForm({ ...form, earning: e.target.value })}
                  placeholder="0.00"
                  data-testid="form-earning-input"
                />
                {shortfall > 0 && (
                  <p className="text-xs text-rose-600 inline-flex items-center gap-1">
                    <ShieldAlert className="h-3.5 w-3.5" />
                    Below guarantee — payable will use {formatINR(minGuarantee)} instead. Shortfall {formatINR(shortfall)}.
                  </p>
                )}
              </div>
              <MoneyInput
                label="Shop Deduction"
                value={form.shop_deduction}
                onChange={(v) => setForm({ ...form, shop_deduction: v })}
                testId="form-shop-deduction-input"
              />
              <MoneyInput
                label="Other Deduction"
                value={form.other_deduction}
                onChange={(v) => setForm({ ...form, other_deduction: v })}
                testId="form-other-deduction-input"
              />
            </div>

            {/* Minimum Guarantee selector */}
            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold inline-flex items-center gap-1.5">
                    <ShieldAlert className="h-3.5 w-3.5" /> Minimum Guarantee
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Applied only when earning is below this amount.
                  </div>
                </div>
                <div className="text-right tabular">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Applied</div>
                  <div className="font-display text-lg font-semibold text-emerald-800">
                    {formatINR(minGuarantee)}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{minGuaranteeSource}</div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Preset</Label>
                  <Select
                    value={form.min_guarantee_preset_id || "__none__"}
                    onValueChange={(v) =>
                      setForm({
                        ...form,
                        min_guarantee_preset_id: v === "__none__" ? "" : v,
                        min_guarantee_override: "",
                      })
                    }
                  >
                    <SelectTrigger data-testid="form-mg-preset-select">
                      <SelectValue placeholder="Boy default / none" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="__none__">Use delivery boy default</SelectItem>
                      {presets.map((p) => (
                        <SelectItem key={p.id} value={p.id} data-testid={`form-mg-option-${p.id}`}>
                          {p.name} — {formatINR(p.amount)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Manual override (optional)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">₹</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      className="currency-input pl-7"
                      value={form.min_guarantee_override}
                      onChange={(e) =>
                        setForm({ ...form, min_guarantee_override: e.target.value })
                      }
                      placeholder={selectedPreset ? String(selectedPreset.amount) : "0.00"}
                      data-testid="form-mg-override-input"
                    />
                  </div>
                </div>
              </div>
              {presets.length === 0 && (
                <p className="mt-3 text-[11px] text-zinc-500">
                  Tip: create presets under <span className="font-medium">Delivery Boys → Minimum Guarantees</span> so you can pick them here.
                </p>
              )}
            </div>
          </section>

          {/* Payment */}
          <section className="bg-white rounded-xl border border-zinc-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-display text-lg font-medium">Payment Received</h2>
                <p className="text-xs text-zinc-500 mt-0.5">Enter any combination — cash + UPI in one entry is supported.</p>
              </div>
              <div className="text-right tabular">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Paid</div>
                <div className="font-display text-xl font-semibold text-emerald-700" data-testid="form-total-paid">
                  {formatINR(totalPaid)}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PmMoneyInput
                label="Cash"
                icon="₹"
                value={form.paid_by_method.cash}
                onChange={(v) => setPbm("cash", v)}
                accent="emerald"
                testId="form-paid-cash-input"
              />
              <PmMoneyInput
                label="UPI"
                icon="📱"
                value={form.paid_by_method.upi}
                onChange={(v) => setPbm("upi", v)}
                accent="blue"
                testId="form-paid-upi-input"
              />
              <PmMoneyInput
                label="Bank Transfer"
                icon="🏦"
                value={form.paid_by_method.bank}
                onChange={(v) => setPbm("bank", v)}
                accent="violet"
                testId="form-paid-bank-input"
              />
              <PmMoneyInput
                label="Other"
                icon="…"
                value={form.paid_by_method.other}
                onChange={(v) => setPbm("other", v)}
                accent="zinc"
                testId="form-paid-other-input"
              />
            </div>

            {/* Denominations grid (only if any cash amount) */}
            {cashPaid > 0 && (
              <div className="mt-6 rounded-xl border border-zinc-200 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b border-zinc-200">
                  <div className="flex items-center gap-2">
                    <Banknote className="h-4 w-4 text-emerald-700" />
                    <div className="font-medium text-sm">Cash Denomination Breakdown</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right tabular">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Denom Total</div>
                      <div className={`font-display font-semibold ${denomMatchesPaid ? "text-emerald-700" : "text-amber-700"}`}>
                        {formatINR(denomTotal)}
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={useDenomAsPaid}
                      data-testid="use-denom-as-paid-btn"
                    >
                      <Calculator className="h-3.5 w-3.5 mr-1" /> Set as Cash Paid
                    </Button>
                  </div>
                </div>
                <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                  {DENOMINATIONS.map((d) => {
                    const count = parseInt(form.denominations[d]) || 0;
                    const subtotal = d * count;
                    return (
                      <div key={d} className="flex items-center gap-2">
                        <div className="w-16 text-sm font-medium tabular text-zinc-700">
                          ₹{d}
                        </div>
                        <span className="text-zinc-400 text-sm">×</span>
                        <Input
                          type="number"
                          min="0"
                          value={form.denominations[d] === 0 ? "" : form.denominations[d]}
                          onChange={(e) => setDenom(d, e.target.value)}
                          placeholder="0"
                          className="currency-input w-20"
                          data-testid={`denom-${d}-input`}
                        />
                        <div className="ml-auto text-sm tabular font-medium text-zinc-700 w-24 text-right">
                          {formatINR(subtotal)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!denomMatchesPaid && cashPaid > 0 && denomTotal > 0 && (
                  <div className="px-4 py-3 border-t border-zinc-200 bg-amber-50 text-amber-800 text-xs flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Denomination total ({formatINR(denomTotal)}) doesn&apos;t match Cash Paid ({formatINR(cashPaid)}).
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Notes */}
          <section className="bg-white rounded-xl border border-zinc-200 p-6">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="mt-1.5"
              placeholder="Anything to remember about this entry…"
              data-testid="form-notes-input"
            />
          </section>
        </div>

        {/* Right: sticky summary */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="bg-white rounded-xl border border-zinc-200 p-6" data-testid="live-summary">
              <h2 className="font-display text-lg font-medium mb-4">Live Summary</h2>
              <div className="space-y-2 text-sm tabular">
                <RowLine label="Cash Collected" value={num(form.cash_collected)} />
                {shortfall > 0 ? (
                  <>
                    <RowLine
                      label={`− Earning (Min. Guarantee)`}
                      value={minGuarantee}
                      muted
                    />
                    <div className="text-[11px] text-rose-600 -mt-1 pl-1">
                      Earning {formatINR(num(form.earning))} lifted to guarantee {formatINR(minGuarantee)}
                    </div>
                  </>
                ) : (
                  <RowLine label="− Earning" value={num(form.earning)} muted />
                )}
                <RowLine label="− Shop Deduction" value={num(form.shop_deduction)} muted />
                <RowLine label="− Other Deduction" value={num(form.other_deduction)} muted />
                <div className="border-t border-zinc-200 my-2" />
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-zinc-500">Final Payable</span>
                  <span className="font-display text-2xl font-semibold" data-testid="summary-payable">
                    {formatINR(finalPayable)}
                  </span>
                </div>
                <RowLine label="− Paid Amount" value={totalPaid} muted />
                <div className="border-t border-zinc-200 my-2" />
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-zinc-500">Balance</span>
                  <span
                    className={`font-display text-2xl font-semibold ${
                      balance > 0 ? "text-amber-700" : balance < 0 ? "text-rose-600" : "text-emerald-700"
                    }`}
                    data-testid="summary-balance"
                  >
                    {formatINR(balance)}
                  </span>
                </div>
              </div>

              {shortfall > 0 && (
                <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3">
                  <div className="flex items-center gap-2 text-xs text-rose-700 font-medium uppercase tracking-widest">
                    <ShieldAlert className="h-3.5 w-3.5" /> Min Guarantee Alert
                  </div>
                  <div className="mt-1 text-sm text-rose-800 tabular">
                    Earning is {formatINR(shortfall)} short of guarantee ({formatINR(minGuarantee)}).
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={saving}
                className="w-full mt-6 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg"
                data-testid="save-entry-btn"
              >
                <Save className="h-4 w-4 mr-1.5" />
                {saving ? "Saving…" : isEdit ? "Save Changes" : "Save Entry"}
              </Button>
            </div>

            <div className="bg-white rounded-xl border border-zinc-200 p-4 text-xs text-zinc-500">
              <div className="font-medium text-zinc-700 mb-1">Formula</div>
              <div className="tabular leading-relaxed">
                Cash Collected − Earning − Shop Ded. − Other Ded. = <span className="text-zinc-900 font-medium">Final Payable</span>
                <br />
                Final Payable − Paid Amount = <span className="text-zinc-900 font-medium">Balance</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </form>
  );
}

const MoneyInput = ({ label, value, onChange, testId, required }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">₹</span>
      <Input
        type="number"
        step="0.01"
        min="0"
        className="currency-input pl-7"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0.00"
        data-testid={testId}
        required={required}
      />
    </div>
  </div>
);

const PmMoneyInput = ({ label, icon, value, onChange, accent, testId }) => {
  const accents = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
    zinc: "border-zinc-200 bg-zinc-50 text-zinc-700",
  }[accent] || "";
  return (
    <div className="space-y-1.5">
      <Label className="inline-flex items-center gap-2">
        <span className={`inline-flex items-center justify-center h-5 w-5 rounded-md border text-[10px] font-bold ${accents}`}>
          {icon}
        </span>
        {label}
      </Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">₹</span>
        <Input
          type="number"
          step="0.01"
          min="0"
          className="currency-input pl-7"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0.00"
          data-testid={testId}
        />
      </div>
    </div>
  );
};

const RowLine = ({ label, value, muted }) => (
  <div className="flex items-center justify-between">
    <span className={muted ? "text-zinc-500" : "text-zinc-700"}>{label}</span>
    <span className={muted ? "text-zinc-600" : "text-zinc-900 font-medium"}>{formatINR(value)}</span>
  </div>
);
